import {
  useMemo,
  useState,
  useEffect,
  useTransition,
  memo,
  useCallback
} from 'react';
import {
  useGetStaffListQuery,
  useDeleteStaffMutation
} from '@/services/staff.service';
import StaffTable from './StaffTable';
import useCommonStore, { setModalConfig } from '@/stores/common';
import toast from 'react-hot-toast';
import InstructorTableSkeleton from '../Skeletons/InstructorTableSkeleton';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '@/constants';

const StaffManager: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isPending, startTransition] = useTransition();
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const { mutateAsync: deleteStaffMutation } = useDeleteStaffMutation();

  const { hideCommonModal } = useCommonStore<{
    hideCommonModal: () => void;
  }>(state => state);

  // Build query string with memoization
  const queryString = useMemo(() => {
    const parts: string[] = ['pagination=false'];

    if (debouncedSearch.trim()) {
      parts.push(`search=${encodeURIComponent(debouncedSearch.trim())}`);
    }

    if (sortField) {
      parts.push(`sortBy=${encodeURIComponent(sortField)}`);
      parts.push(`sortOrder=${encodeURIComponent(sortOrder)}`);
    }

    return parts.join('&');
  }, [debouncedSearch, sortField, sortOrder]);

  const {
    data: staffResponse,
    refetch,
    isLoading,
    isFetching
  } = useGetStaffListQuery(queryString);

  // Memoized data processing
  const { staffList } = useMemo(() => {
    const responseData = staffResponse as any;
    const data = responseData?.data || {};
    const items = data?.items || [];

    return {
      staffList: items
    };
  }, [staffResponse]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 800);
    return () => clearTimeout(timer);
  }, [search]);

  // Memoized delete confirmation handler
  const handleConfirmDelete = useCallback(
    async (staffId: number) => {
      startTransition(async () => {
        try {
          const response = await deleteStaffMutation(staffId);
          if (response?.success) {
            toast.success(
              response?.message ||
                'Staff deleted successfully. Financial data will be re-generated.'
            );
            refetch();
            hideCommonModal();
          }
        } catch {
          toast.error('Failed to delete staff member');
        }
      });
    },
    [deleteStaffMutation, refetch, hideCommonModal]
  );

  // Memoized delete handler
  const handleDeleteStaff = useCallback(
    (staffId: number, staffName: string) => {
      setModalConfig({
        visible: true,
        type: 'confirmModal',
        data: {
          title: 'Delete Staff Member',
          message: `Are you sure you want to delete ${staffName}? This action cannot be undone and will re-generate associated financial data and reports.`
        },
        onClick: () => handleConfirmDelete(staffId)
      });
    },
    [handleConfirmDelete]
  );

  // Memoized sort handler
  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        // If same field, toggle order
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
      } else {
        // If different field, set to ascending
        setSortField(field);
        setSortOrder('ASC');
      }
    },
    [sortField, sortOrder]
  );

  // Memoized search change handler
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    []
  );

  const clearSearch = useCallback(() => {
    setSearch('');
  }, []);

  const handleAddStaff = useCallback(() => {
    navigate(ROUTE_PATHS.STAFF_MANAGER_ADD);
  }, [navigate]);

  return (
    <main
      className='p-3 md:p-4 lg:p-6 bg-gradient-to-br from-slate-50 to-white'
      role='main'
    >
      <div className='mb-4 md:mb-6'>
        <div className='flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4'>
          <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
            <label
              htmlFor='search'
              className='text-slate-600 text-sm md:text-base font-medium'
            >
              Search:
            </label>
            <div className='relative'>
              <input
                id='search'
                type='text'
                value={search}
                onChange={handleSearchChange}
                placeholder='Search staff by name, email, phone...'
                className='border border-slate-300 rounded-lg px-2 py-1.5 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                aria-describedby={search ? 'clear-search' : undefined}
                autoComplete='off'
              />
              {search && (
                <button
                  id='clear-search'
                  onClick={clearSearch}
                  className='absolute right-8 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
                  title='Clear search'
                  type='button'
                  aria-label='Clear search'
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <button
            className='bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 self-start lg:self-auto'
            onClick={handleAddStaff}
          >
            Add Staff
          </button>
        </div>
      </div>

      {/* Staff Table Section */}
      <section aria-label='Staff list' aria-busy={isPending || isLoading}>
        {isLoading ? (
          <InstructorTableSkeleton />
        ) : (
          <StaffTable
            staffList={staffList}
            isPending={isPending || isFetching}
            handleDeleteStaff={handleDeleteStaff}
            handleSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
          />
        )}
      </section>
    </main>
  );
};

export default memo(StaffManager);
